import { useState } from 'react'
import { pushSupport, usePushDevice, usePushSettings } from '../lib/push'
import {
  allDayPresets,
  defaultReminderMinutes,
  formatAllDayMoment,
  formatDelay,
  joinDelay,
  reminderPresets,
  reminderUnits,
  sameMoment,
  splitDelay,
  type ReminderChoice,
  type ReminderUnit,
} from '../lib/reminders'

const inputClass =
  'mt-1.5 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-neutral-900 outline-none transition focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100 dark:focus:border-neutral-100'
const labelClass = 'block text-sm font-medium text-neutral-800 dark:text-neutral-200'

/**
 * Choix du rappel d'un evenement : un delai avant son debut, ou, pour un
 * evenement sur la journee, un jour et une heure d'ici.
 */
export default function ReminderField({
  choice,
  onChange,
  isAllDay,
  firstDay,
}: {
  choice: ReminderChoice
  onChange: (choice: ReminderChoice) => void
  isAllDay: boolean
  /** Premier jour de l'evenement (`AAAA-MM-JJ`), pour nommer un moment qui n'est pas un des choix proposes. */
  firstDay: string
}) {
  return (
    <div className="space-y-3">
      {isAllDay ? (
        <AllDayReminder choice={choice} onChange={onChange} firstDay={firstDay} />
      ) : (
        <DelayReminder choice={choice} onChange={onChange} />
      )}
      {choice.kind !== 'none' && <DeviceNotice />}
    </div>
  )
}

function DelayReminder({ choice, onChange }: { choice: ReminderChoice; onChange: (choice: ReminderChoice) => void }) {
  const minutes = choice.kind === 'minutes' ? choice.minutes : null
  const proposed = minutes !== null && (reminderPresets as readonly number[]).includes(minutes)
  // « Personnalise » reste ouvert meme quand la saisie retombe sur un delai propose.
  const [custom, setCustom] = useState(minutes !== null && !proposed)
  const initial = splitDelay(minutes !== null && Number.isInteger(minutes) ? minutes : defaultReminderMinutes)
  const [amount, setAmount] = useState(String(initial.amount))
  const [unit, setUnit] = useState<ReminderUnit>(initial.unit)

  const selected = minutes === null ? '' : custom ? 'custom' : String(minutes)

  return (
    <>
      <label className="block">
        <span className={labelClass}>Rappel</span>
        <select
          value={selected}
          onChange={(event) => {
            const value = event.target.value
            setCustom(value === 'custom')
            if (value === '') {
              onChange({ kind: 'none' })
            } else if (value === 'custom') {
              onChange({ kind: 'minutes', minutes: joinDelay(amount, unit) })
            } else {
              onChange({ kind: 'minutes', minutes: Number(value) })
            }
          }}
          className={inputClass}
        >
          <option value="">Aucun</option>
          {reminderPresets.map((preset) => (
            <option key={preset} value={preset}>
              {formatDelay(preset)}
            </option>
          ))}
          <option value="custom">Personnalise...</option>
        </select>
      </label>

      {selected === 'custom' && (
        <div className="flex items-center gap-2">
          <input
            aria-label="Delai du rappel"
            inputMode="numeric"
            value={amount}
            maxLength={5}
            onChange={(event) => {
              setAmount(event.target.value)
              onChange({ kind: 'minutes', minutes: joinDelay(event.target.value, unit) })
            }}
            className={`${inputClass} mt-0 w-24`}
          />
          <select
            aria-label="Unite du delai"
            value={unit}
            onChange={(event) => {
              const next = event.target.value as ReminderUnit
              setUnit(next)
              onChange({ kind: 'minutes', minutes: joinDelay(amount, next) })
            }}
            className={`${inputClass} mt-0 w-auto`}
          >
            {reminderUnits.map((item) => (
              <option key={item.unit} value={item.unit}>
                {item.label}
              </option>
            ))}
          </select>
          <span className="text-sm text-neutral-600 dark:text-neutral-400">avant le debut</span>
        </div>
      )}
    </>
  )
}

function AllDayReminder({
  choice,
  onChange,
  firstDay,
}: {
  choice: ReminderChoice
  onChange: (choice: ReminderChoice) => void
  firstDay: string
}) {
  const moment = choice.kind === 'allDay' ? choice : null
  const preset = moment === null ? -1 : allDayPresets.findIndex((item) => sameMoment(item, moment))

  return (
    <label className="block">
      <span className={labelClass}>Rappel</span>
      <select
        value={moment === null ? '' : preset === -1 ? 'other' : String(preset)}
        onChange={(event) => {
          const picked = allDayPresets[Number(event.target.value)]
          if (event.target.value === '') {
            onChange({ kind: 'none' })
          } else if (picked !== undefined) {
            onChange({ kind: 'allDay', daysBefore: picked.daysBefore, hour: picked.hour, minute: picked.minute })
          }
        }}
        className={inputClass}
      >
        <option value="">Aucun</option>
        {/* Regle depuis un autre fuseau, ou avant un changement d'heure : il se garde tel quel. */}
        {moment !== null && preset === -1 && <option value="other">{formatAllDayMoment(moment, firstDay)}</option>}
        {allDayPresets.map((item, index) => (
          <option key={item.label} value={index}>
            {item.label}
          </option>
        ))}
      </select>
    </label>
  )
}

/** Un rappel est une notification : sans appareil qui les recoit, il ne se voit nulle part. */
function DeviceNotice() {
  const settings = usePushSettings()
  const device = usePushDevice(settings.data?.publicKey)

  if (settings.data === undefined) {
    return null
  }

  const text =
    settings.data.publicKey === null
      ? "Les notifications ne sont pas configurees sur ce serveur : aucun rappel ne partira."
      : pushSupport() !== 'supported' || device.data === false
        ? "Les notifications ne sont pas actives sur cet appareil : le rappel n'arrivera que sur ceux ou vous les avez activees, depuis votre profil."
        : null

  return text === null ? null : <p className="text-xs text-amber-700 dark:text-amber-300">{text}</p>
}
